import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

const LanguageSwitcher = () => {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const languages = [
    { code: 'en', name: 'English', flag: '🇬🇧', label: 'EN' },
    { code: 'it', name: 'Italiano', flag: '🇮🇹', label: 'IT' },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪', label: 'DE' },
    { code: 'fr', name: 'Français', flag: '🇫🇷', label: 'FR' },
    { code: 'zh', name: '中文', flag: '🇨🇳', label: 'ZH' },
    { code: 'he', name: 'עברית', flag: '🇮🇱', label: 'HE' },
    { code: 'ar', name: 'العربية', flag: '🇸🇦', label: 'AR' },
    { code: 'ru', name: 'Русский', flag: '🇷🇺', label: 'RU' },
    { code: 'es', name: 'Español', flag: '🇪🇸', label: 'ES' },
  ];

  const currentLanguage = languages.find((lang) => lang.code === i18n.language) || languages[0];

  const changeLanguage = (langCode: string) => {
    i18n.changeLanguage(langCode);
    localStorage.setItem('preferredLanguage', langCode);
    
    // Set document direction for RTL languages
    if (langCode === 'he' || langCode === 'ar') {
      document.documentElement.dir = 'rtl';
      document.documentElement.lang = langCode;
    } else {
      document.documentElement.dir = 'ltr';
      document.documentElement.lang = langCode;
    }
    
    setIsOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Set initial direction on mount
  useEffect(() => {
    const currentLang = i18n.language;
    if (currentLang === 'he' || currentLang === 'ar') {
      document.documentElement.dir = 'rtl';
    } else {
      document.documentElement.dir = 'ltr';
    }
    document.documentElement.lang = currentLang;
  }, [i18n.language]);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-lg transition-all duration-300 text-white border border-white/20 whitespace-nowrap cursor-pointer"
        aria-label="Select language"
      >
        <i className="ri-global-line text-lg"></i>
        <span className="font-medium">{currentLanguage.label}</span>
        <i className={`ri-arrow-down-s-line text-lg transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}></i>
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg py-2 z-50 max-h-[400px] overflow-y-auto">
          {languages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => changeLanguage(lang.code)}
              className={`w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors duration-200 flex items-center justify-between gap-3 cursor-pointer whitespace-nowrap ${
                i18n.language === lang.code ? 'bg-teal-50 text-teal-600' : 'text-gray-700'
              }`}
            >
              <span className="font-medium">{lang.name}</span>
              <span className="text-sm opacity-70">{lang.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;
